import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ImagePlus, X } from 'lucide-react'

import { createCommunityPostRecord, resolveCommunityAuthorRole, type CommunityPostType } from '@/lib/communityPostData'
import {
  buildCommunityBoardId,
  cityLabelFromBoardId,
  isArtistOnlyBoard,
} from '@/lib/communityBoards'
import { isPostableCommunityBoardId } from '@/lib/boardFromLocation'
import { uploadCommunityPostCarouselImage } from '@/lib/communityImageUpload'
import { invalidateQueryCache, uploadAndCreatePortfolioItem } from '@/lib/portfolioUpload'
import {
  COMPRESS_IMAGES_ENDPOINT,
  EMBED_PORTFOLIO_ITEMS_ENDPOINT,
} from '@/config/endpoints'

import { useAuthState } from '@/hooks/useAuth'
import { useCities } from '@/hooks/useCities'
import { useUserData } from '@/hooks/useUserData'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AuthModal } from '@/components/auth/AuthModal'
import { Toast, useToast } from '@/components/ui/toast'

const MAX_CAROUSEL_IMAGES = 8
const MIN_TITLE_CHARS = 4

const ARTIST_POST_TYPE_OPTIONS: readonly CommunityPostType[] = [
  'general',
  'flash_drop',
  'new_tattoo',
  'deal',
]

const composerFieldGroupClassName = 'space-y-2'
const composerFieldLabelClassName = 'text-sm font-medium'
const composerSelectTriggerClassName = 'h-10 w-full border-input'

async function triggerPortfolioBackgroundPipelines(portfolioItemIds: readonly string[]): Promise<void> {
  if (portfolioItemIds.length === 0) return
  try {
    await fetch(COMPRESS_IMAGES_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }).catch((err) => console.warn('[CommunityComposer] compress trigger skipped', err))
    await fetch(EMBED_PORTFOLIO_ITEMS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_ids: [...portfolioItemIds] }),
    }).catch((err) => console.warn('[CommunityComposer] embed trigger skipped', err))
    await invalidateQueryCache()
  } catch (err) {
    console.warn('[CommunityComposer] Background pipeline error', err)
  }
}

function buildAuthorDisplayName(params: {
  displayName?: string | null
  instagram?: string | null
  email?: string | null
}): string | null {
  if (params.displayName?.trim().length) return params.displayName.trim()
  if (params.instagram?.trim().length) return `@${params.instagram.trim()}`
  if (params.email?.trim().length) return params.email.trim()
  return null
}

export default function CommunityPostComposerPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { user: authUser, loading: authLoading } = useAuthState()
  const { cities: launchedCities, cityCountryByCity } = useCities()
  const { userData, isArtist, artistId } = useUserData()
  const { toast, showToast, hideToast } = useToast()

  const boardIdFromUrl = searchParams.get('board')?.trim() ?? ''

  const initialCityLabelFromUrl = useMemo(() => {
    if (!isPostableCommunityBoardId(boardIdFromUrl)) return null
    return cityLabelFromBoardId(boardIdFromUrl)
  }, [boardIdFromUrl])

  const [selectedCityLabel, setSelectedCityLabel] = useState<string | null>(initialCityLabelFromUrl)

  const [postType, setPostType] = useState<CommunityPostType>('general')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [mirrorToPortfolio, setMirrorToPortfolio] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)

  const sortedLaunchedCities = useMemo(
    () => [...launchedCities].sort((leftCity, rightCity) => leftCity.localeCompare(rightCity)),
    [launchedCities],
  )

  useEffect(() => {
    if (!authLoading && authUser == null) {
      setAuthModalOpen(true)
    }
  }, [authLoading, authUser])

  useEffect(() => {
    if (initialCityLabelFromUrl) {
      setSelectedCityLabel(initialCityLabelFromUrl)
    }
  }, [initialCityLabelFromUrl])

  const boardId = useMemo(() => {
    if (!selectedCityLabel) return ''
    return buildCommunityBoardId('everyone', { kind: 'city', cityLabel: selectedCityLabel })
  }, [selectedCityLabel])

  const cityLabel = selectedCityLabel

  const localPreviewUrls = useMemo(
    () => imageFiles.map((file) => URL.createObjectURL(file)),
    [imageFiles],
  )

  useEffect(() => {
    return () => {
      for (const url of localPreviewUrls) URL.revokeObjectURL(url)
    }
  }, [localPreviewUrls])

  const boardScopeLabel = cityLabel ?? String(t('community.composer.placeholders.city'))
  const audienceLabel = isArtistOnlyBoard(boardId)
    ? String(t('community.audienceArtists'))
    : String(t('community.audienceEveryone'))
  const backHref = '/board'
  const shouldMirrorPortfolioByDefault = postType === 'new_tattoo' || postType === 'flash_drop'

  function addImageFiles(picked: FileList | null) {
    if (!picked || picked.length === 0) return
    setImageFiles((current) => {
      const merged = [...current, ...Array.from(picked)]
      return merged.slice(0, MAX_CAROUSEL_IMAGES)
    })
  }

  function removeImageFileAt(indexToRemove: number) {
    setImageFiles((current) => current.filter((_, i) => i !== indexToRemove))
  }

  async function publishPost() {
    const viewerUid = authUser?.uid
    if (!viewerUid) {
      setAuthModalOpen(true)
      return
    }

    const trimmedTitle = title.trim()
    if (trimmedTitle.length < MIN_TITLE_CHARS) {
      showToast(String(t('community.composer.validationTitle')))
      return
    }

    if (!cityLabel || !boardId.length || !isPostableCommunityBoardId(boardId)) {
      showToast(String(t('community.composer.validationCity')))
      return
    }

    if (isArtistOnlyBoard(boardId) && !isArtist) {
      showToast(String(t('community.artistsDeskOnlyPosting')))
      return
    }

    if (postType === 'flash_drop' && imageFiles.length === 0) {
      showToast(String(t('board.composer.validationFlashImages')))
      return
    }

    setPublishing(true)
    try {
      const uploadedImageUrls: string[] = []
      for (const file of imageFiles) {
        const url = await uploadCommunityPostCarouselImage(viewerUid, file)
        uploadedImageUrls.push(url)
      }

      const mirroredPortfolioIds: string[] = []

      const shouldMirrorToPortfolio =
        isArtist &&
        shouldMirrorPortfolioByDefault &&
        mirrorToPortfolio &&
        typeof artistId === 'string' &&
        artistId.length > 0 &&
        imageFiles.length > 0

      if (shouldMirrorToPortfolio) {
        const filesToMirror = postType === 'flash_drop' ? imageFiles : [imageFiles[0]!]
        for (const sourceFile of filesToMirror) {
          const portfolioItemId = await uploadAndCreatePortfolioItem(sourceFile, artistId!)
          mirroredPortfolioIds.push(portfolioItemId)
        }
      }

      const authorDisplayName = buildAuthorDisplayName({
        displayName: userData?.displayName,
        instagram: userData?.instagram,
        email: authUser.email,
      })

      const linkedItems =
        mirroredPortfolioIds.length > 0
          ? mirroredPortfolioIds.map((portfolioItemId) => ({
              type: 'portfolioItem' as const,
              id: portfolioItemId,
            }))
          : []

      await createCommunityPostRecord({
        city: cityLabel,
        country: cityCountryByCity?.[cityLabel] ?? null,
        audience: isArtistOnlyBoard(boardId) ? 'artists' : 'everyone',
        userId: viewerUid,
        authorDisplayName,
        authorRole: resolveCommunityAuthorRole({
          isAdmin: userData?.isAdmin,
          isArtist,
        }),
        artistId: isArtist && typeof artistId === 'string' ? artistId : null,
        postType,
        title: trimmedTitle,
        body: body.trim().length > 0 ? body.trim() : null,
        imageUrls: uploadedImageUrls,
        linkedItems,
      })

      if (mirroredPortfolioIds.length > 0) {
        await triggerPortfolioBackgroundPipelines(mirroredPortfolioIds)
      }

      navigate(backHref)
    } catch (err) {
      console.error('[CommunityComposer] Publish failed', err)
      showToast(String(t('community.composer.publishFailed')))
    } finally {
      setPublishing(false)
    }
  }

  const canShowMirrorToPortfolioToggle =
    isArtist && shouldMirrorPortfolioByDefault && imageFiles.length > 0
  const canPublish =
    !publishing &&
    title.trim().length >= MIN_TITLE_CHARS &&
    cityLabel != null &&
    boardId.length > 0
  const bodyRows = postType === 'new_tattoo' ? 8 : 5

  return (
    <div className="min-h-screen bg-background pb-32">
      <Toast message={toast.message} isVisible={toast.isVisible} onClose={hideToast} />

      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border/50">
        <div className="phone-container-padded py-3">
          <div className="flex min-w-0 items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="h-8 w-8 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
            >
              <Link to={backHref} aria-label={String(t('community.composer.backAria'))}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="min-w-0 flex-1 space-y-1">
              <h1 className="text-page-title text-foreground truncate">
                {t('community.composer.pageTitle')}
              </h1>
              {cityLabel ? (
                <p className="text-xs text-muted-foreground truncate">
                  {t('community.composer.postingTo', {
                    place: boardScopeLabel,
                    audience: audienceLabel,
                  })}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <form
        className="phone-container-padded space-y-3 pt-4"
        onSubmit={(event) => {
          event.preventDefault()
          void publishPost()
        }}
      >
        <div className={composerFieldGroupClassName}>
          <div className={composerFieldLabelClassName}>{t('community.composer.fields.city')}</div>
          <Select
            value={selectedCityLabel ?? undefined}
            onValueChange={(nextCityLabel) => setSelectedCityLabel(nextCityLabel)}
          >
            <SelectTrigger className={composerSelectTriggerClassName}>
              <SelectValue placeholder={String(t('community.composer.placeholders.city'))} />
            </SelectTrigger>
            <SelectContent>
              {sortedLaunchedCities.map((cityName) => (
                <SelectItem key={cityName} value={cityName}>
                  {cityName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className={composerFieldGroupClassName}>
          <div className={composerFieldLabelClassName}>{t('community.composer.fields.title')}</div>
          <Input
            autoComplete="off"
            placeholder={String(t('community.composer.placeholders.title'))}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={200}
          />
        </div>

        <div className={composerFieldGroupClassName}>
          <div className={composerFieldLabelClassName}>{t('community.composer.fields.body')}</div>
          <Textarea
            placeholder={String(
              postType === 'new_tattoo'
                ? t('board.composer.placeholders.newTattooBody')
                : t('community.composer.placeholders.body'),
            )}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={bodyRows}
          />
        </div>

        <div className={composerFieldGroupClassName}>
          <div className="flex items-baseline justify-between gap-3">
            <span className={composerFieldLabelClassName}>
              {t('community.composer.fields.images')}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('community.composer.imagesOptional')}
            </span>
          </div>

          {imageFiles.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {imageFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted"
                >
                  <img
                    src={localPreviewUrls[index]}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImageFileAt(index)}
                    className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white"
                    aria-label={String(t('community.composer.removeImageAria'))}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  {index === 0 && (
                    <span className="absolute left-1.5 bottom-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
                      {t('community.composer.coverBadge')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {imageFiles.length < MAX_CAROUSEL_IMAGES && (
            <label
              className={`flex items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-4 py-6 text-sm text-muted-foreground transition-colors hover:bg-muted/60 ${
                publishing ? 'pointer-events-none opacity-50' : 'cursor-pointer'
              }`}
            >
              <ImagePlus className="w-5 h-5" />
              <span>
                {imageFiles.length === 0
                  ? t('community.composer.addPhotosCta')
                  : t('community.composer.addMorePhotosCta', {
                      remaining: MAX_CAROUSEL_IMAGES - imageFiles.length,
                    })}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(event) => {
                  addImageFiles(event.target.files)
                  event.target.value = ''
                }}
              />
            </label>
          )}
        </div>

        {isArtist && (
          <div className={composerFieldGroupClassName}>
            <div className={composerFieldLabelClassName}>{t('board.composer.postTypeLabel')}</div>
            <div className="grid grid-cols-2 gap-2">
              {ARTIST_POST_TYPE_OPTIONS.map((option) => {
                const active = postType === option
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setPostType(option)
                      if (option === 'new_tattoo' || option === 'flash_drop') {
                        setMirrorToPortfolio(true)
                      }
                    }}
                    className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                      active
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border bg-background text-foreground/80 hover:bg-accent'
                    }`}
                  >
                    <span className="font-medium">{t(`board.postTypes.${option}`)}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {canShowMirrorToPortfolioToggle && (
          <div className="rounded-lg border border-border bg-card p-3">
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                id="community-composer-mirror-portfolio"
                checked={mirrorToPortfolio}
                onCheckedChange={(checked) => setMirrorToPortfolio(checked === true)}
                className="mt-0.5"
              />
              <span className="text-sm leading-snug">
                <span className="font-medium">
                  {postType === 'flash_drop'
                    ? t('board.composer.mirrorFlashDropTitle')
                    : t('community.composer.mirrorToggleTitle')}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {postType === 'flash_drop'
                    ? t('board.composer.mirrorFlashDropHint')
                    : t('community.composer.mirrorToggleHint')}
                </span>
              </span>
            </label>
          </div>
        )}
      </form>

      <div
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 backdrop-blur-sm"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="phone-container-padded py-3">
          <Button
            type="button"
            className="w-full h-14 text-base font-medium rounded-[0.6rem]"
            disabled={!canPublish}
            onClick={() => void publishPost()}
          >
            {publishing
              ? t('community.composer.publishingBusy')
              : t('community.composer.submit')}
          </Button>
        </div>
      </div>

      <AuthModal
        dismissible
        isOpen={authModalOpen}
        onOpenChange={setAuthModalOpen}
        onSuccess={() => setAuthModalOpen(false)}
        source="community-composer"
      />
    </div>
  )
}
